import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { canPerformAuthOperation, recordAuthOperation, getAuthRateLimitStatus } from '@/utils/authRateLimiter';
import { logAuthEvent } from '@/utils/authDebugger';
import { clearAuthCacheAndRefresh } from '@/utils/cacheHelper';
import { logger } from '@/services/logger';
import { isSpecialAdmin, checkSubscriptionWithFallback } from '@/utils/admin-fix';
import { authCircuitBreaker } from '@/utils/auth-circuit-breaker';

// Define types for subscription data
export type SubscriptionPlan = {
  id: string;
  product_id: string;
  active: boolean;
  description: string;
  unit_amount: number;
  currency: string;
  type: string;
  interval: string;
  interval_count: number;
  trial_period_days: number | null;
  stripe_price_id?: string;
  created_at?: string;
  metadata?: any;
  products: {
    id: string;
    name: string;
    description: string;
    active: boolean;
    image: string | null;
    metadata: any;
  };
};

export type Subscription = {
  id: string;
  user_id: string;
  status: 'trialing' | 'active' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'unpaid' | 'trial';
  metadata?: Record<string, any>;
  cancel_at_period_end: boolean;
  created?: string;
  created_at?: string;
  current_period_start: string;
  current_period_end: string;
  ended_at: string | null;
  cancel_at: string | null;
  canceled_at: string | null;
  trial_start: string | null;
  trial_end: string | null;
  price_id?: string;
  quantity?: number;
  prices: SubscriptionPlan;
  updated_at?: string;
};

type SubscriptionContextType = {
  subscription: Subscription | null;
  isLoading: boolean;
  plans: SubscriptionPlan[];
  refreshSubscription: (force?: boolean) => Promise<boolean | void>;
  isSubscriptionActive: boolean;
  isTrialing: boolean;
  daysRemaining: number | null;
  currentPlan: SubscriptionPlan | null;
  hasError: boolean;
  startSubscriptionPolling: (checkoutSessionId?: string) => void;
  stopSubscriptionPolling: () => void;
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// Export the refreshSubscription function for use outside the context
let isProviderInitialized = false;
let pendingRefreshes: boolean[] = [];

// Create a promise that will resolve when the provider is initialized
let providerInitPromise: Promise<void>;
let resolveProviderInit: () => void;

// Initialize the promise
providerInitPromise = new Promise<void>((resolve) => {
  resolveProviderInit = resolve;
});

let globalRefreshSubscription: (force?: boolean) => Promise<boolean | void> = async (force) => {
  if (!isProviderInitialized) {
    console.log('Waiting for subscription provider to initialize');
    // Queue the refresh request to be processed once the provider is initialized
    pendingRefreshes.push(force || false);
    
    try {
      // Wait for the provider to initialize with a timeout
      const timeoutPromise = new Promise<void>((_, reject) => {
        setTimeout(() => reject(new Error('Provider initialization timeout')), 5000);
      });
      
      await Promise.race([providerInitPromise, timeoutPromise]);
      console.log('Provider initialized, proceeding with refresh');
      return true;
    } catch (error) {
      console.log('Timed out waiting for provider initialization');
      return false;
    }
  }
  
  // This should never be reached if isProviderInitialized is false
  console.warn('Unexpected code path in refreshSubscription');
  return false;
};

export const refreshSubscription = (force?: boolean) => globalRefreshSubscription(force);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [pollingTimeout, setPollingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [plansLoading, setPlansLoading] = useState(true);

  // Add a debounce mechanism to prevent too many refreshes
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(0);
  const [lastAuthEvent, setLastAuthEvent] = useState<string>('');
  const [authEventCount, setAuthEventCount] = useState<number>(0);
  const REFRESH_COOLDOWN_MS = 2000; // 2 seconds cooldown between refreshes
  const MAX_AUTH_EVENTS_PER_MINUTE = 3; // Limit auth events per minute
  
  // Internal refresh function with all the state access
  const internalRefreshSubscription = async (force?: boolean) => {
    if (!subscription && !force) {
      console.log('Subscription not initialized, skipping refresh');
      setIsLoading(false);
      return false;
    }
    
    try {
      console.log('Refreshing subscription');
      
      // Check rate limiter for session operations
      if (!canPerformAuthOperation('session_check')) {
        console.warn('Auth rate limiter blocked session check, skipping refresh');
        setIsLoading(false);
        return false;
      }
      
      recordAuthOperation('session_check');
      
      // Get the current user first
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session?.user) {
        // No user found, skip the rest of the process
        console.log('No authenticated user found, skipping subscription refresh');
        setIsLoading(false);
        setSubscription(null);
        return false;
      }
      
      const user = sessionData.session.user;
      
      // Check for subscription state to prevent unnecessary reloads
      // especially important for special users to prevent loops
      if (subscription && !force) {
        // If we already have a subscription object and force isn't true, 
        // then for admin accounts, we can just skip the refresh entirely
        const adminEmails = ['simmakin@gmail.com', 'simmakin@googlemail.com'];
        if (user?.email && adminEmails.includes(user.email)) {
          console.log('Admin user with existing subscription data, skipping refresh');
          setIsLoading(false);
          return true;
        }
        
        // For regular users with active subscriptions, also avoid refreshing too often
        if (['active', 'trialing'].includes(subscription.status)) {
          const now = Date.now();
          // Refresh active subscriptions at most every 5 minutes
          if (now - lastRefreshTime < 5 * 60 * 1000) {
            console.log('Active subscription refreshed recently, skipping');
            setIsLoading(false);
            return true;
          }
        }
      }
      
      // Special case for specific email addresses - add caching to prevent loops
      if (user.email === 'simmakin@gmail.com' || user.email === 'simmakin@googlemail.com') {
        // Check last admin subscription creation time
        const lastAdminSubscriptionTime = localStorage.getItem(`last_admin_subscription_${user.id}`);
        const now = Date.now();
        
        // If we've created an admin subscription in the last minute, reuse it
        if (lastAdminSubscriptionTime && (now - parseInt(lastAdminSubscriptionTime)) < 60000) { // 1 minute
          console.log('Reusing recent admin subscription, skipping recreation');
          // Still keep the admin in reset mode, but don't recreate the subscription object
          setIsLoading(false);
          return true;
        }
        
        console.log('Special admin user, creating admin subscription');
        // Create admin subscription without calling Stripe
        const adminSubscription = createAdminSubscription(user.id);
        console.log('Admin subscription created:', { 
          status: adminSubscription.status,
          price_id: adminSubscription.price_id,
          prices: adminSubscription.prices ? 'valid' : 'missing'
        });
        
        // Store the timestamp to prevent loops
        localStorage.setItem(`last_admin_subscription_${user.id}`, now.toString());
        
        setSubscription(adminSubscription);
        setIsLoading(false);
        return true;
      }
      
      // Check if this is a special admin using the new utility
      if (await isSpecialAdmin()) {
        console.log('Special admin detected via admin-fix utility');
        const adminSubscription = createAdminSubscription(user.id);
        setSubscription(adminSubscription);
        setIsLoading(false);
        return true;
      }
      
      // Regular cooldown check - only for non-admin users
      const now = Date.now();
      if (isLoading || (now - lastRefreshTime < REFRESH_COOLDOWN_MS)) {
        // Skip this refresh if we're already loading or refreshed recently
        console.log('Skipping subscription refresh (cooldown or already in progress)');
        setIsLoading(false);
        return false;
      }
      
      setLastRefreshTime(now);
      setIsLoading(true);
      setHasError(false);
      
      // Use the new subscription checking utility with fallback
      const hasActiveSubscription = await checkSubscriptionWithFallback(user.id);
      
      if (hasActiveSubscription) {
        // Get detailed subscription information
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('user_subscription_status')
          .select('*')
          .eq('user_id', user.id)
          .limit(1)
          .single();
        
        if (!subscriptionError && subscriptionData) {
          setSubscription(subscriptionData);
          console.log('Active subscription found:', subscriptionData.status);
                 } else {
           // Create a basic active subscription object if detailed data is not available
           const basicSubscription: Subscription = {
             id: `basic_${user.id}`,
             user_id: user.id,
             status: 'active' as const,
             cancel_at_period_end: false,
             current_period_start: new Date().toISOString(),
             current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
             ended_at: null,
             cancel_at: null,
             canceled_at: null,
             trial_start: null,
             trial_end: null,
             price_id: 'fallback-price-id',
             prices: {
               id: 'fallback-price-id',
               product_id: 'fallback-product-id',
               active: true,
               description: 'Active Subscription',
               unit_amount: 0,
               currency: 'gbp',
               type: 'recurring',
               interval: 'month',
               interval_count: 1,
               trial_period_days: null,
               products: {
                 id: 'fallback-product-id',
                 name: 'Active Plan',
                 description: 'Active subscription plan',
                 active: true,
                 image: null,
                 metadata: {}
               }
             },
             created_at: new Date().toISOString(),
             updated_at: new Date().toISOString()
           };
           setSubscription(basicSubscription);
           console.log('Created basic active subscription for user');
         }
      } else {
        setSubscription(null);
        console.log('No active subscription found for user');
      }
      
      setIsLoading(false);
      return hasActiveSubscription;
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      setHasError(true);
      setIsLoading(false);
      return false;
    }
  };

  // Helper function to create admin subscription object
  const createAdminSubscription = (userId: string): Subscription => {
    const now = new Date().toISOString();
    const yearFromNow = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();
    
    return {
      id: 'admin-access',
      user_id: userId,
      status: 'active',
      cancel_at_period_end: false,
      created_at: now,
      current_period_start: now,
      current_period_end: yearFromNow, // 1 year from now
      ended_at: null,
      cancel_at: null,
      canceled_at: null,
      trial_start: null,
      trial_end: null,
      price_id: 'admin-price-id', // Add explicit price_id to ensure it's not null
      prices: {
        id: 'admin-price-id',
        product_id: 'admin-product-id',
        active: true,
        description: 'Admin Access',
        unit_amount: 0,
        currency: 'USD',
        type: 'recurring',
        interval: 'year',
        interval_count: 1,
        trial_period_days: null,
        products: {
          id: 'admin-product-id',
          name: 'Admin Access',
          description: 'Full administrative access',
          active: true,
          image: null,
          metadata: {}
        }
      }
    };
  };

  // Set up auth state change listener to refresh subscription on login/logout
  useEffect(() => {
    let authEventDebounceTimer: NodeJS.Timeout | null = null;
    let lastAuthEventTime = 0;
    
    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      logAuthEvent(event, 'SubscriptionContext', session?.user?.id);
      console.log('Auth state changed:', event, session?.user?.id);
      
      // Check rate limiter first
      if (!canPerformAuthOperation('auth_change')) {
        console.warn('Auth rate limiter blocked auth state change processing');
        logAuthEvent(`${event}_BLOCKED`, 'SubscriptionContext', session?.user?.id);
        return;
      }
      
      recordAuthOperation('auth_change');
      
      const now = Date.now();
      
      // Handle SIGNED_OUT immediately
      if (event === 'SIGNED_OUT') {
        console.log('User signed out, clearing subscription data');
        setSubscription(null);
        setIsLoading(false);
        stopSubscriptionPolling();
        setAuthEventCount(0);
        setLastAuthEvent('');
        return;
      }
      
      // For other events, implement aggressive debouncing to prevent loops
      if (['SIGNED_IN', 'TOKEN_REFRESHED', 'USER_UPDATED'].includes(event)) {
        // Count auth events to detect potential loops
        if (event === lastAuthEvent && now - lastAuthEventTime < 5000) {
          // Same event within 5 seconds - increment counter
          const newCount = authEventCount + 1;
          setAuthEventCount(newCount);
          
                   // If we've seen too many of the same event, skip processing
         if (newCount > MAX_AUTH_EVENTS_PER_MINUTE) {
           console.warn(`Too many ${event} events (${newCount}), skipping to prevent loop`);
           
           // Check if we should trigger emergency cache clear
           if (newCount > MAX_AUTH_EVENTS_PER_MINUTE * 2) {
             console.error('Critical auth loop detected, clearing cache and refreshing');
             logAuthEvent('CRITICAL_LOOP_DETECTED', 'SubscriptionContext', session?.user?.id);
             
             toast({
               title: "Authentication Issue Detected",
               description: "Clearing cache to resolve login issues...",
               variant: "destructive",
             });
             
             // Clear cache and refresh after a short delay
             setTimeout(() => {
               clearAuthCacheAndRefresh();
             }, 2000);
           }
           
           return;
         }
        } else {
          // Different event or enough time has passed - reset counter
          setAuthEventCount(1);
        }
        
        setLastAuthEvent(event);
        lastAuthEventTime = now;
        
        // For TOKEN_REFRESHED events, be much more conservative
        if (event === 'TOKEN_REFRESHED') {
          // Check circuit breaker first
          if (!authCircuitBreaker.checkTokenRefresh()) {
            console.log('TOKEN_REFRESHED: Circuit breaker blocked refresh');
            return;
          }
          
          // Only refresh subscription for TOKEN_REFRESHED if we don't have a subscription yet
          // or if it's been more than 10 minutes since last refresh
          if (!subscription || (now - lastRefreshTime > 10 * 60 * 1000)) {
            console.log('TOKEN_REFRESHED: Refreshing subscription (no existing subscription or old data)');
            
            // Clear any existing debounce timer
            if (authEventDebounceTimer) {
              clearTimeout(authEventDebounceTimer);
            }
            
            // Debounce TOKEN_REFRESHED events more aggressively (5 seconds)
            authEventDebounceTimer = setTimeout(() => {
              refreshSubscription();
            }, 5000);
          } else {
            console.log('TOKEN_REFRESHED: Skipping subscription refresh (have recent data)');
          }
        } else {
          // For SIGNED_IN and USER_UPDATED, refresh more readily but still debounce
          console.log(`Refreshing subscription due to auth event: ${event}`);
          
          // Clear any existing debounce timer
          if (authEventDebounceTimer) {
            clearTimeout(authEventDebounceTimer);
          }
          
          // Shorter debounce for important events (1 second)
          authEventDebounceTimer = setTimeout(() => {
            refreshSubscription();
          }, 1000);
        }
      }
    });
    
    // Clean up listener on unmount
    return () => {
      if (authEventDebounceTimer) {
        clearTimeout(authEventDebounceTimer);
      }
      authListener?.subscription.unsubscribe();
      stopSubscriptionPolling();
    };
  }, [subscription, lastRefreshTime, authEventCount, lastAuthEvent]);

  // Add periodic verification to catch existing users who may have slipped through
  useEffect(() => {
    // Initial check for existing session
    const verifyExistingUser = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session?.user) {
        const lastVerification = localStorage.getItem(`last_complete_verification_${sessionData.session.user.id}`);
        const now = Date.now();
        
        // Run a complete verification on app load if it hasn't been done in 24 hours
        if (!lastVerification || (now - parseInt(lastVerification)) > 24 * 60 * 60 * 1000) {
          console.log('Running complete verification for existing user:', sessionData.session.user.id);
          
          try {
            // Import and use the forceVerifySubscription function
            const { forceVerifySubscription } = await import('@/utils/subscription-checker');
            await forceVerifySubscription(sessionData.session.user.id);
            
            // Update last verification timestamp
            localStorage.setItem(`last_complete_verification_${sessionData.session.user.id}`, now.toString());
            
            // Refresh subscription data after verification
            await refreshSubscription();
          } catch (error) {
            console.error('Error during complete verification:', error);
          }
        }
      }
    };
    
    verifyExistingUser();
    
    // Set up periodic verification (every 30 minutes)
    const verificationInterval = setInterval(async () => {
      const { data: currentSession } = await supabase.auth.getSession();
      if (currentSession?.session?.user) {
        console.log('Running periodic subscription verification');
        await refreshSubscription();
      }
    }, 30 * 60 * 1000);
    
    return () => {
      clearInterval(verificationInterval);
    };
  }, []);
  
  // Start polling for subscription changes after checkout completion
  const startSubscriptionPolling = (checkoutSessionId?: string) => {
    // Clear any existing polling
    if (pollingTimeout) {
      clearTimeout(pollingTimeout);
    }
    
    // Reset polling attempts
    setPollingAttempts(0);
    
    // Define the polling function
    const poll = async () => {
      try {
        // Force refresh to bypass caching
        const refreshed = await refreshSubscription(true);
        
        // Update polling attempts
        setPollingAttempts(prev => prev + 1);
        
        // Check if subscription is now active
        if (subscription?.status === 'active' || subscription?.status === 'trialing') {
          console.log('Subscription now active, stopping polling');
          
          // If there's a checkout session ID, clean up
          if (checkoutSessionId) {
            localStorage.removeItem('pendingCheckoutSessionId');
          }
          
          return;
        }
        
        // Continue polling if we haven't reached the maximum attempts
        if (pollingAttempts < 10) {
          // Exponential backoff with a max of 10 seconds
          const delay = Math.min(2000 * Math.pow(1.5, pollingAttempts), 10000);
          
          console.log(`Subscription not active yet, polling again in ${delay}ms`);
          const timeoutId = setTimeout(poll, delay) as unknown as NodeJS.Timeout;
          setPollingTimeout(timeoutId);
        } else {
          console.log('Max polling attempts reached, stopping');
        }
      } catch (error) {
        console.error('Error polling subscription:', error);
      }
    };
    
    // Start polling
    poll();
  };
  
  // Stop polling for subscription changes
  const stopSubscriptionPolling = () => {
    if (pollingTimeout) {
      clearTimeout(pollingTimeout);
      setPollingTimeout(null);
    }
  };
  
  // Set up the global refresh function and mark provider as initialized
  useEffect(() => {
    // Update the global refresh function to use our internal one
    globalRefreshSubscription = internalRefreshSubscription;
    
    // Mark the provider as initialized
    isProviderInitialized = true;
    
    // Resolve the initialization promise to unblock any waiting calls
    if (typeof resolveProviderInit === 'function') {
      console.log('Resolving provider initialization promise');
      resolveProviderInit();
    }
    
    // Process any pending refreshes that were queued before initialization
    if (pendingRefreshes.length > 0) {
      console.log(`Processing ${pendingRefreshes.length} pending subscription refreshes`);
      const force = pendingRefreshes.some(f => f === true);
      pendingRefreshes = [];
      setTimeout(() => {
        internalRefreshSubscription(force);
      }, 500); // Small delay to ensure everything is properly set up
    } else {
      // If no pending refreshes, do an initial load after a short delay
      setTimeout(() => {
        internalRefreshSubscription();
      }, 500);
    }
    
    // Cleanup when component unmounts
    return () => {
      isProviderInitialized = false;
      pendingRefreshes = [];
      
      // Reset the initialization promise for next mount
      providerInitPromise = new Promise<void>((resolve) => {
        resolveProviderInit = resolve;
      });
    };
  }, []);

  

  // Calculate additional properties
  const isSubscriptionActive = !!subscription && 
    (subscription.status === 'active' || subscription.status === 'trialing' || subscription.status === 'trial');
  
  const isTrialing = !!subscription && (subscription.status === 'trialing' || subscription.status === 'trial');
  
  const daysRemaining = subscription?.current_period_end 
    ? Math.max(0, Math.ceil((new Date(subscription.current_period_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  
  // Create a fallback price if prices is missing but the subscription is active
  const currentPlan = subscription?.prices || (subscription && isSubscriptionActive ? {
    id: subscription.price_id || 'default-price-id',
    product_id: 'default-product-id',
    active: true,
    description: 'Subscription',
    unit_amount: 0,
    currency: 'USD',
    type: 'recurring',
    interval: 'month',
    interval_count: 1,
    trial_period_days: null,
    products: {
      id: 'default-product-id',
      name: 'Subscription',
      description: 'Active subscription',
      active: true,
      image: null,
      metadata: {}
    }
  } : null);
  
  // Update the global refresh function when the component mounts or internalRefreshSubscription changes
  useEffect(() => {
    // This will be updated when the component mounts
    globalRefreshSubscription = internalRefreshSubscription;
    
    return () => {
      // Reset to the default implementation when unmounting
      globalRefreshSubscription = async () => {
        console.warn('refreshSubscription called after provider unmount');
        return false;
      };
    };
  }, [internalRefreshSubscription]);
  
  // Combine loading states
  const isContextLoading = isLoading || plansLoading;

  const value = {
    subscription,
    isLoading: isContextLoading,
    plans,
    refreshSubscription,
    isSubscriptionActive,
    isTrialing,
    daysRemaining,
    currentPlan,
    hasError,
    startSubscriptionPolling,
    stopSubscriptionPolling
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
